import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  type Api,
  markNotificationAsRead,
  markNotificationAsUnread,
} from "api-client";
import { Icon } from "icons";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui";

import { getNotificationsQueryKey } from "src/queries/notifications";

interface NotificationItemMenuProps {
  notification: Api.GetAllNotifications.Output.Notification;
}

export function NotificationItemMenu(props: NotificationItemMenuProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const notificationId = props.notification.id;
  const isRead = props.notification.read;

  const toggleReadMutation = useMutation({
    mutationFn: function () {
      if (isRead) {
        return markNotificationAsUnread({ body: { notificationId } });
      }

      return markNotificationAsRead({ body: { notificationId } });
    },
    onSuccess: function () {
      queryClient.invalidateQueries({ queryKey: getNotificationsQueryKey() });
    },
  });

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t(
            "notificationItem.actionsAriaLabel",
            "Notification actions",
          )}
          className="absolute top-3 right-3 flex size-7 items-center justify-center rounded-full bg-white text-gray-600 opacity-0 shadow-sm transition-opacity group-hover:opacity-100 hover:bg-gray-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
        >
          <Icon name="tabler-dots" className="size-4" strokeWidth={1.8} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={function () {
            toggleReadMutation.mutate();
          }}
        >
          <Icon
            name={isRead ? "tabler-mail" : "tabler-check"}
            className="mr-2 size-4"
            strokeWidth={1.8}
          />
          {isRead
            ? t("notificationItem.markAsUnread", "Mark as unread")
            : t("notificationItem.markAsRead", "Mark as read")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
